import React, { useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import Notification from '@this/src/notification';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { TableCell, TableRow } from '@this/src/components/shared/ui/data_displays/table';
import type HotelOrderItemAgodaTotal from '@this/src/domain/arrangement/hotel_order_item_agoda_total';
import type Arranger from '@this/src/domain/arranger/arranger';
import type { ArrangerArgs } from '@this/src/domain/arranger/arranger';
import { Fetcher } from '@this/src/util';

interface Props {
  hotelOrderItemAgodaTotal: HotelOrderItemAgodaTotal;
  arrangers: Arranger[];
  currentArranger: Arranger | null;
  suppliedItemId: number | null;
}

interface ApproveResponse {
  hotel_order_item_agoda_total_approval: {
    reservation_date: string;
    approved_at: string;
    approved_by: ArrangerArgs;
    supplied_item_id: number | null;
  };
}

const TotalTableRow: React.FC<Props> = observer(
  ({ hotelOrderItemAgodaTotal, arrangers, currentArranger, suppliedItemId }) => {
    const [loading, setLoading] = useState(false);
    const reservationDate = hotelOrderItemAgodaTotal.reservationDate.format('YYYY-MM-DD');
    const hotelElementCsvUrl = `/arrangement/auto_reservation_inventory/agoda_hotel/total/hotel_element_csv?reservation_date=${reservationDate}`;
    const agodaCsvUrl = `/arrangement/auto_reservation_inventory/agoda_hotel/total/agoda_csv?reservation_date=${reservationDate}`;

    const handleApprove = useCallback(() => {
      setLoading(true);
      Fetcher.post<ApproveResponse>('/arrangement/auto_reservation_inventory/agoda_hotel/total/confirm', {
        reservation_date: reservationDate,
        supplied_item_id: suppliedItemId
      })
        .then(res => {
          const approval = res.hotel_order_item_agoda_total_approval;
          hotelOrderItemAgodaTotal.setApprovedAt(approval.approved_at);
          hotelOrderItemAgodaTotal.setApprovedBy(approval.approved_by?.id as number);
          hotelOrderItemAgodaTotal.setSuppliedItemId(approval.supplied_item_id as number);
          Notification.success(`${approval.reservation_date}の確認が完了しました`);
        })
        .catch(() => {
          Notification.error('確認に失敗しました');
        })
        .finally(() => setLoading(false));
    }, [hotelOrderItemAgodaTotal, reservationDate, suppliedItemId]);

    const handleCancel = useCallback(() => {
      Fetcher.delete('/arrangement/auto_reservation_inventory/agoda_hotel/total/cancel', {
        reservation_date: reservationDate
      })
        .then(() => {
          hotelOrderItemAgodaTotal.setApprovedAt(undefined);
          hotelOrderItemAgodaTotal.setApprovedBy(undefined);
          Notification.success('確認を取り消しました');
        })
        .catch(() => {
          Notification.error('確認の取り消しに失敗しました');
        });
    }, [hotelOrderItemAgodaTotal, reservationDate]);

    return (
      <TableRow>
        <TableCell>{reservationDate}</TableCell>
        <TableCell align="right">{hotelOrderItemAgodaTotal.hotelElementTotal?.toLocaleString() || 0}</TableCell>
        <TableCell align="right">
          {hotelOrderItemAgodaTotal.orderItemOriginalPriceTotal?.toLocaleString() || 0}
        </TableCell>
        <TableCell align="right">
          {hotelOrderItemAgodaTotal.agodaReservationTotal?.toLocaleString() || 0}
        </TableCell>
        <TableCell align="right">
          {hotelOrderItemAgodaTotal.orderItemPriceDetailsPriceTotal?.toLocaleString() || 0}
        </TableCell>
        <TableCell align="right">
          {hotelOrderItemAgodaTotal.orderItemOriginalPricePriceTotal?.toLocaleString() || 0}
        </TableCell>
        <TableCell align="right">
          {hotelOrderItemAgodaTotal.agodaPriceUnconvertible
            ? '換算不可'
            : hotelOrderItemAgodaTotal.agodaReservationPriceTotal?.toLocaleString() || 0}
        </TableCell>
        <TableCell>{hotelOrderItemAgodaTotal.exactMatch ? '○' : '×'}</TableCell>
        <TableCell>
          <Button href={hotelElementCsvUrl} isExternal>
            Hotel Element CSV
          </Button>
        </TableCell>
        <TableCell>
          <Button href={agodaCsvUrl} isExternal>
            Agoda CSV
          </Button>
        </TableCell>
        <TableCell>{hotelOrderItemAgodaTotal.approvedAt?.format('YYYY-MM-DD') || '─'}</TableCell>
        <TableCell>
          {hotelOrderItemAgodaTotal.approvedBy ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>
                  {arrangers.find(arranger => arranger.id === hotelOrderItemAgodaTotal.approvedBy)?.name}
                </span>
                {currentArranger?.id === hotelOrderItemAgodaTotal.approvedBy && (
                  <Button size="small" onClick={handleCancel}>
                    ×
                  </Button>
                )}
              </div>
              {hotelOrderItemAgodaTotal.suppliedItemId &&
                hotelOrderItemAgodaTotal.suppliedItemId !== suppliedItemId && (
                  <div style={{ fontSize: '11px', color: '#a72c09' }}>承認時と仕入商品が異なります</div>
                )}
            </div>
          ) : (
            <Button onClick={handleApprove} loading={loading}>
              確認
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  }
);

export default TotalTableRow;
